import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  ActiveSurveyForm,
  Count,
  Form,
  Forms,
  ResponseForm,
  ResponseSurveyQuestion,
  SatisfactionOverviewDetails,
  SatisfactionSurveyActiveForm,
  SatisfactionSurveyDetail,
  SatisfactionSurveyForm,
  SatisfactionSurveyQuestion,
  SatisfactionSurveyQuestions,
  SatisfactionSurveyRequest,
  SatisfactionSurveySummary,
  SurveyDetail,
  SurveyOverviewDetails,
  SurveyQuestion,
  SurveyQuestions,
  SurveyRequest,
  SurveyService,
  SurveySummary,
} from "@/api";
import _ from "lodash";

type SatisfactionSurveyOverview = {
  total: number;
  data: Array<SatisfactionOverviewDetails>;
};

type SurveyState = {
  beginAt: string | undefined;
  endAt: string | undefined;
  activeDischargeForm: ActiveSurveyForm | null;
  dischargeOverview: SurveyOverviewDetails | null;
  dischargeQuestions: SurveyQuestions | null;
  dischargeForms: Forms | null;
  dischargeSummary: Array<SurveySummary> | null;
  dischargeOverviewDetails: Array<SurveyDetail> | null;
  activeAdmissionForm: ActiveSurveyForm | null;
  admissionOverview: SurveyOverviewDetails | null;
  admissionQuestions: SurveyQuestions | null;
  admissionForms: Forms | null;
  admissionSummary: Array<SurveySummary> | null;
  admissionOverviewDetails: Array<SurveyDetail> | null;
  admissionCount: Count | null;
  dischargeCount: Count | null;
  activeSatisfactionForm: SatisfactionSurveyActiveForm | null;
  satisfactionOverview: SatisfactionSurveyOverview | null;
  satisfactionQuestions: SatisfactionSurveyQuestions | null;
  satisfactionForms: Array<SatisfactionSurveyForm> | null;
  satisfactionSummary: Array<SatisfactionSurveySummary> | null;
  satisfactionOverviewDetails: Array<SatisfactionSurveyDetail> | null;
  satisfactionCount: Count | null;
};

const state: SurveyState = {
  beginAt: undefined,
  endAt: undefined,
  activeDischargeForm: null,
  dischargeOverview: null,
  dischargeQuestions: null,
  dischargeForms: null,
  dischargeSummary: null,
  dischargeOverviewDetails: null,
  activeAdmissionForm: null,
  admissionOverview: null,
  admissionQuestions: null,
  admissionForms: null,
  admissionSummary: null,
  admissionOverviewDetails: null,
  admissionCount: null,
  dischargeCount: null,
  activeSatisfactionForm: null,
  satisfactionOverview: null,
  satisfactionQuestions: null,
  satisfactionForms: null,
  satisfactionSummary: null,
  satisfactionOverviewDetails: null,
  satisfactionCount: null,
};

const getters: GetterTree<SurveyState, RootState> = {
  beginAt: state => state.beginAt,
  endAt: state => state.endAt,
  activeDischargeForm: state => state.activeDischargeForm,
  dischargeOverview: state => state.dischargeOverview,
  dischargeQuestions: state => state.dischargeQuestions,
  dischargeForms: state => state.dischargeForms,
  dischargeSummary: state => state.dischargeSummary,
  dischargeOverviewDetails: state => state.dischargeOverviewDetails,
  activeAdmissionForm: state => state.activeAdmissionForm,
  admissionOverview: state => state.admissionOverview,
  admissionQuestions: state => state.admissionQuestions,
  admissionForms: state => state.admissionForms,
  admissionSummary: state => state.admissionSummary,
  admissionOverviewDetails: state => state.admissionOverviewDetails,
  admissionCount: state => state.admissionCount,
  dischargeCount: state => state.dischargeCount,
  activeSatisfactionForm: state => state.activeSatisfactionForm,
  satisfactionOverview: state => state.satisfactionOverview,
  satisfactionQuestions: state => state.satisfactionQuestions,
  satisfactionForms: state => state.satisfactionForms,
  satisfactionSummary: state => state.satisfactionSummary,
  satisfactionOverviewDetails: state => state.satisfactionOverviewDetails,
  satisfactionCount: state => state.satisfactionCount,
};

const mutations: MutationTree<SurveyState> = {
  SET_ADMISSION_COUNT: (state, count) => {
    state.admissionCount = count;
  },
  SET_DISCHARGE_COUNT: (state, count) => {
    state.dischargeCount = count;
  },
  SET_BEGIN_AT: (state, beginAt) => {
    state.beginAt = beginAt;
  },
  SET_END_AT: (state, endAt) => {
    state.endAt = endAt;
  },
  SET_ACTIVE_DISCHARGE_FORM: (state, form) => {
    state.activeDischargeForm = form;
  },
  SET_DISCHARGE_OVERVIEW: (state, overview) => {
    state.dischargeOverview = overview;
  },
  SET_DISCHARGE_QUESTIONS: (state, questions) => {
    state.dischargeQuestions = questions;
  },
  ADD_DISCHARGE_QUESTION: (state, question) => {
    if (state.dischargeQuestions == null) {
      state.dischargeQuestions = [];
    }
    state.dischargeQuestions?.push(question);
  },
  UPDATE_DISCHARGE_QUESTION: (state, question) => {
    let index = state.dischargeQuestions?.findIndex(
      (item: ResponseSurveyQuestion) => item.id == question.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.dischargeQuestions?.splice(index, 1);
      state.dischargeQuestions?.push(question);
      state.dischargeQuestions = _.sortBy(state.dischargeQuestions, ["order"]);
    }
  },
  REMOVE_DISCHARGE_QUESTION: (state, question) => {
    let index = state.dischargeQuestions?.findIndex(
      (item: ResponseSurveyQuestion) => item.id == question.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.dischargeQuestions?.splice(index, 1);
    }
  },
  SET_DISCHARGE_FORMS: (state, forms) => {
    state.dischargeForms = forms;
  },
  ADD_DISCHARGE_FORM: (state, form) => {
    if (state.dischargeForms == null) {
      state.dischargeForms = [];
    }
    state.dischargeForms.push(form);
  },
  REMOVE_DISCHARGE_FORM: (state, form) => {
    let index = state.dischargeForms?.findIndex(
      (item: ResponseForm) => item.id == form.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.dischargeForms?.splice(index, 1);
    }
  },
  UPDATE_DISCHARGE_FORM: (state, form) => {
    let index = state.dischargeForms?.findIndex(
      (item: ResponseForm) => item.id == form.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.dischargeForms?.splice(index, 1);
      state.dischargeForms?.push(form);
      state.dischargeForms = _.sortBy(state.dischargeForms, ["order"]);
    }
  },
  SET_DISCHARGE_SUMMARY: (state, summary) => {
    state.dischargeSummary = summary;
  },
  SET_DISCHARGE_OVERVIEW_DETAILS: (state, overview) => {
    state.dischargeOverviewDetails = overview;
  },
  SET_ACTIVE_ADMISSION_FORM: (state, form) => {
    state.activeAdmissionForm = form;
  },
  SET_ADMISSION_OVERVIEW: (state, overview) => {
    state.admissionOverview = overview;
  },
  SET_ADMISSION_QUESTIONS: (state, questions) => {
    state.admissionQuestions = questions;
  },
  ADD_ADMISSION_QUESTION: (state, question) => {
    if (state.admissionQuestions == null) {
      state.admissionQuestions = [];
    }
    state.admissionQuestions?.push(question);
  },
  UPDATE_ADMISSION_QUESTION: (state, question) => {
    let index = state.admissionQuestions?.findIndex(
      (item: ResponseSurveyQuestion) => item.id == question.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.admissionQuestions?.splice(index, 1);
      state.admissionQuestions?.push(question);
      state.admissionQuestions = _.sortBy(state.admissionQuestions, ["order"]);
    }
  },
  REMOVE_ADMISSION_QUESTION: (state, question) => {
    let index = state.admissionQuestions?.findIndex(
      (item: ResponseSurveyQuestion) => item.id == question.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.admissionQuestions?.splice(index, 1);
    }
  },
  SET_ADMISSION_FORMS: (state, forms) => {
    state.admissionForms = forms;
  },
  ADD_ADMISSION_FORM: (state, form) => {
    if (state.admissionForms == null) {
      state.admissionForms = [];
    }
    state.admissionForms.push(form);
  },
  REMOVE_ADMISSION_FORM: (state, form) => {
    let index = state.admissionForms?.findIndex(
      (item: ResponseForm) => item.id == form.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.admissionForms?.splice(index, 1);
    }
  },
  UPDATE_ADMISSION_FORM: (state, form) => {
    let index = state.admissionForms?.findIndex(
      (item: ResponseForm) => item.id == form.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.admissionForms?.splice(index, 1);
      state.admissionForms?.push(form);
    }
  },
  SET_ADMISSION_SUMMARY: (state, summary) => {
    state.admissionSummary = summary;
  },
  SET_ADMISSION_OVERVIEW_DETAILS: (state, overview) => {
    state.admissionOverviewDetails = overview;
  },
  SET_ACTIVE_SATISFACTION_FORM: (state, form) => {
    state.activeSatisfactionForm = form;
  },
  SET_SATISFACTION_OVERVIEW: (state, overview) => {
    state.satisfactionOverview = overview;
  },
  SET_SATISFACTION_QUESTIONS: (state, questions) => {
    state.satisfactionQuestions = questions;
  },
  ADD_SATISFACTION_QUESTION: (state, question) => {
    if (state.satisfactionQuestions == null) {
      state.satisfactionQuestions = [];
    }
    state.satisfactionQuestions?.push(question);
  },
  UPDATE_SATISFACTION_QUESTION: (state, question) => {
    let index = state.satisfactionQuestions?.findIndex(
      (item: SatisfactionSurveyQuestion) => item.id == question.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.satisfactionQuestions?.splice(index, 1);
      state.satisfactionQuestions?.push(question);
      state.satisfactionQuestions = _.sortBy(state.satisfactionQuestions, [
        "order",
      ]);
    }
  },
  REMOVE_SATISFACTION_QUESTION: (state, question) => {
    let index = state.satisfactionQuestions?.findIndex(
      (item: SatisfactionSurveyQuestion) => item.id == question.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.satisfactionQuestions?.splice(index, 1);
    }
  },
  SET_SATISFACTION_FORMS: (state, forms) => {
    state.satisfactionForms = forms;
  },
  ADD_SATISFACTION_FORM: (state, form) => {
    if (state.satisfactionForms == null) {
      state.satisfactionForms = [];
    }
    state.satisfactionForms.push(form);
  },
  REMOVE_SATISFACTION_FORM: (state, form) => {
    let index = state.satisfactionForms?.findIndex(
      (item: ResponseForm) => item.id == form.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.satisfactionForms?.splice(index, 1);
    }
  },
  UPDATE_SATISFACTION_FORM: (state, form) => {
    let index = state.satisfactionForms?.findIndex(
      (item: ResponseForm) => item.id == form.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.satisfactionForms?.splice(index, 1);
      state.satisfactionForms?.push(form);
      state.satisfactionForms = _.sortBy(state.satisfactionForms, ["order"]);
    }
  },
  SET_SATISFACTION_SUMMARY: (state, summary) => {
    state.satisfactionSummary = summary;
  },
  SET_SATISFACTION_OVERVIEW_DETAILS: (state, overview) => {
    state.satisfactionOverviewDetails = overview;
  },
  SET_SATISFACTION_COUNT: (state, count) => {
    state.satisfactionCount = count;
  },
};

const actions: ActionTree<SurveyState, RootState> = {
  fetchAdmissionSurveyCounts: async ({ commit, rootState }) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const counts = await SurveyService.getAdmissionSurveyCount({ companyId });
      commit("SET_ADMISSION_COUNT", counts);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchDischargeSurveyCounts: async ({ commit, rootState }) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const counts = await SurveyService.getDischargeSurveyCount({ companyId });
      commit("SET_DISCHARGE_COUNT", counts);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchSatisfactionSurveyCounts: async ({ commit, rootState }) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const counts = await SurveyService.getSatisfactionSurveyCount({
        companyId,
      });
      commit("SET_SATISFACTION_COUNT", counts);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  setBeginAt: ({ commit }, beginAt?: string) => {
    commit("SET_BEGIN_AT", beginAt);
  },
  setEndAt: ({ commit }, endAt?: string) => {
    commit("SET_END_AT", endAt);
  },
  fetchActiveSurveyForm: async (
    { rootState, commit, getters },
    force = false,
  ) => {
    const discharge = getters["activeDischargeForm"];
    const admission = getters["activeAdmissionForm"];
    const satisfaction = getters["activeSatisfactionForm"];
    if (!force) {
      if (discharge.id && admission.id && satisfaction.id) {
        return;
      }
    }
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const admissionForm = await SurveyService.getActiveAdmissionForm({
        companyId,
      });
      const dischargeForm = await SurveyService.getActiveDischargeForm({
        companyId,
      });
      const satisfactionForm = await SurveyService.getActiveSatisfactionForm({
        companyId,
      });
      commit("SET_ACTIVE_ADMISSION_FORM", admissionForm);
      commit("SET_ACTIVE_DISCHARGE_FORM", dischargeForm);
      commit("SET_ACTIVE_SATISFACTION_FORM", satisfactionForm);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchQuestions: async (
    { commit, rootState },
    { formId, dispatchType }: { formId: number; dispatchType: string },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            const questions = await SurveyService.getAdmissionQuestions({
              companyId,
              formId,
            });
            commit("SET_ADMISSION_QUESTIONS", questions);
          }
          break;
        case "discharge":
          {
            const questions = await SurveyService.getDischargeQuestions({
              companyId,
              formId,
            });
            commit("SET_DISCHARGE_QUESTIONS", questions);
          }
          break;
        case "satisfaction":
          {
            const questions = await SurveyService.getSatisfactionQuestions({
              companyId,
              formId,
            });
            commit("SET_SATISFACTION_QUESTIONS", questions);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  addQuestion: async (
    { commit, rootState },
    {
      requestBody,
      satisfactionRequestBody,
      formId,
      dispatchType,
    }: {
      requestBody: SurveyQuestion;
      satisfactionRequestBody: SatisfactionSurveyQuestion;
      formId: number;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            const question = await SurveyService.addAdmissionQuestion({
              companyId,
              formId,
              requestBody,
            });
            commit("ADD_ADMISSION_QUESTION", question);
          }
          break;
        case "discharge":
          {
            const question = await SurveyService.addDischargeQuestion({
              companyId,
              formId,
              requestBody,
            });
            commit("ADD_DISCHARGE_QUESTION", question);
          }
          break;
        case "satisfaction":
          {
            const question = await SurveyService.addSatisfactionQuestion({
              companyId,
              formId,
              requestBody: satisfactionRequestBody,
            });
            commit("ADD_SATISFACTION_QUESTION", question);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateQuestion: async (
    { commit, rootState },
    {
      requestBody,
      satisfactionRequestBody,
      formId,
      dispatchType,
    }: {
      requestBody: ResponseSurveyQuestion;
      satisfactionRequestBody: SatisfactionSurveyQuestion;
      formId: number;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            const question = await SurveyService.updateAdmissionQuestion({
              companyId,
              formId,
              requestBody,
            });
            commit("UPDATE_ADMISSION_QUESTION", question);
          }
          break;
        case "discharge":
          {
            const question = await SurveyService.updateDischargeQuestion({
              companyId,
              formId,
              requestBody,
            });
            commit("UPDATE_DISCHARGE_QUESTION", question);
          }
          break;
        case "satisfaction":
          {
            const question = await SurveyService.updateSatisfactionQuestion({
              companyId,
              formId,
              requestBody: satisfactionRequestBody,
            });
            commit("UPDATE_SATISFACTION_QUESTION", question);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  removeQuestion: async (
    { commit, rootState },
    {
      requestBody,
      satisfactionRequestBody,
      formId,
      dispatchType,
    }: {
      requestBody: ResponseSurveyQuestion;
      satisfactionRequestBody: SatisfactionSurveyQuestion;
      formId: number;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            const question = await SurveyService.deleteAdmissionQuestion({
              companyId,
              formId,
              requestBody,
            });
            commit("REMOVE_ADMISSION_QUESTION", question);
          }
          break;
        case "discharge":
          {
            const question = await SurveyService.deleteDischargeQuestion({
              companyId,
              formId,
              requestBody,
            });
            commit("REMOVE_DISCHARGE_QUESTION", question);
          }
          break;
        case "satisfaction":
          {
            const question = await SurveyService.deleteSatisfactionQuestion({
              companyId,
              formId,
              requestBody: satisfactionRequestBody,
            });
            commit("REMOVE_SATISFACTION_QUESTION", question);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  sortQuestion: async (
    { commit, rootState },
    {
      requestBody,
      satisfactionRequestBody,
      formId,
      dispatchType,
    }: {
      requestBody: SurveyQuestions;
      satisfactionRequestBody: SatisfactionSurveyQuestions;
      formId: number;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            commit("SET_ADMISSION_QUESTIONS", requestBody);
            await SurveyService.sortAdmissionQuestion({
              companyId,
              requestBody,
              formId,
            });
          }
          break;
        case "discharge":
          {
            commit("SET_DISCHARGE_QUESTIONS", requestBody);
            await SurveyService.sortDischargeQuestion({
              companyId,
              requestBody,
              formId,
            });
          }
          break;
        case "satisfaction":
          {
            commit("SET_SATISFACTION_QUESTIONS", satisfactionRequestBody);
            await SurveyService.sortSatisfactionQuestion({
              companyId,
              requestBody: satisfactionRequestBody,
              formId,
            });
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchForms: async ({ commit, rootState }, dispatchType: string) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            const forms = await SurveyService.getAdmissionForms({ companyId });
            commit("SET_ADMISSION_FORMS", forms);
          }
          break;
        case "discharge":
          {
            const forms = await SurveyService.getDischargeForms({ companyId });
            commit("SET_DISCHARGE_FORMS", forms);
          }
          break;
        case "satisfaction":
          {
            const forms = await SurveyService.getSatisfactionForms({
              companyId,
            });
            commit("SET_SATISFACTION_FORMS", forms);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  addForm: async (
    { commit, rootState },
    { name, dispatchType }: { name: string; dispatchType: string },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const requestBody = {
        companyId,
        name,
        status: 1,
        isStandard: false,
      } as Form;
      switch (dispatchType) {
        case "admission":
          {
            const form = await SurveyService.addAdmissionForm({
              companyId,
              requestBody,
            });
            commit("ADD_ADMISSION_FORM", form);
          }
          break;
        case "discharge":
          {
            const form = await SurveyService.addDischargeForm({
              companyId,
              requestBody,
            });
            commit("ADD_DISCHARGE_FORM", form);
          }
          break;
        case "satisfaction":
          {
            const requestBody = {
              companyId,
              name,
              status: 1,
              isStandard: false,
            } as SatisfactionSurveyForm;
            const forms = await SurveyService.addSatisfactionForm({
              companyId,
              requestBody,
            });
            commit("ADD_SATISFACTION_FORM", forms);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  removeForm: async (
    { commit, rootState },
    {
      requestBody,
      satisfactionRequestBody,
      dispatchType,
    }: {
      requestBody: ResponseForm;
      satisfactionRequestBody: SatisfactionSurveyForm;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            const form = await SurveyService.deleteAdmissionForm({
              companyId,
              requestBody,
            });
            commit("REMOVE_ADMISSION_FORM", form);
          }
          break;
        case "discharge":
          {
            const form = await SurveyService.deleteDischargeForm({
              companyId,
              requestBody,
            });
            commit("REMOVE_DISCHARGE_FORM", form);
          }
          break;
        case "satisfaction":
          {
            const form = await SurveyService.deleteSatisfactionForm({
              companyId,
              requestBody: satisfactionRequestBody,
            });
            commit("REMOVE_SATISFACTION_FORM", form);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateForm: async (
    { commit, rootState },
    {
      requestBody,
      satisfactionRequestBody,
      dispatchType,
    }: {
      requestBody: ResponseForm;
      satisfactionRequestBody: SatisfactionSurveyForm;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            const form = await SurveyService.updateAdmissionForm({
              companyId,
              requestBody,
            });
            commit("UPDATE_ADMISSION_FORM", form);
          }
          break;
        case "discharge":
          {
            const form = await SurveyService.updateDischargeForm({
              companyId,
              requestBody,
            });
            commit("UPDATE_DISCHARGE_FORM", form);
          }
          break;
        case "satisfaction":
          {
            const form = await SurveyService.updateSatisfactionForm({
              companyId,
              requestBody: satisfactionRequestBody,
            });
            commit("UPDATE_SATISFACTION_FORM", form);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  copyCreateForm: async (
    { commit, rootState },
    {
      requestBody,
      satisfactionRequestBody,
      formId,
      dispatchType,
    }: {
      requestBody: Form;
      satisfactionRequestBody: SatisfactionSurveyForm;
      formId: number;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      switch (dispatchType) {
        case "admission":
          {
            const form = await SurveyService.copyAdmissionForm({
              companyId,
              formId,
              requestBody,
            });
            commit("ADD_ADMISSION_FORM", form);
          }
          break;
        case "discharge":
          {
            const form = await SurveyService.copyDischargeForm({
              companyId,
              formId,
              requestBody,
            });
            commit("ADD_DISCHARGE_FORM", form);
          }
          break;
        case "satisfaction":
          {
            const form = await SurveyService.copySatisfactionForm({
              companyId,
              formId,
              requestBody: satisfactionRequestBody,
            });
            commit("ADD_SATISFACTION_FORM", form);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  // TODO: refactor with vue component
  submitSurvey: async (
    { commit, rootState, dispatch },
    {
      requestBody,
      satisfactionRequestBody,
      dispatchType,
    }: {
      requestBody: SurveyRequest;
      satisfactionRequestBody: SatisfactionSurveyRequest;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      switch (dispatchType) {
        case "admission":
          {
            await SurveyService.addAdmissionSurvey({ companyId, requestBody });
          }
          break;
        case "discharge":
          {
            await SurveyService.addDischargeSurvey({ companyId, requestBody });
          }
          break;
        case "satisfaction":
          {
            await SurveyService.addSatisfactionSurvey({
              companyId,
              requestBody: satisfactionRequestBody,
            });
          }
          break;
      }
      dispatch("fetchOverview", { dispatchType });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchSummary: async (
    { commit, rootState },
    {
      beginAt,
      endAt,
      floorId,
      roomId,
      physicianId,
      dispatchType,
    }: {
      beginAt?: string;
      endAt?: string;
      floorId?: number;
      roomId?: number;
      physicianId?: number;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const payload = {
        companyId,
        beginAt,
        endAt,
        floorId,
        physicianId,
        roomId,
      };
      switch (dispatchType) {
        case "admission":
          {
            const summary = await SurveyService.getAdmissionSurveySummary(
              payload,
            );
            commit("SET_ADMISSION_SUMMARY", summary);
          }
          break;
        case "discharge":
          {
            const summary = await SurveyService.getDischargeSurveySummary(
              payload,
            );
            commit("SET_DISCHARGE_SUMMARY", summary);
          }
          break;
        case "satisfaction":
          {
            const summary = await SurveyService.getSatisfactionSurveySummary(
              payload,
            );
            commit("SET_SATISFACTION_SUMMARY", summary);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchOverview: async (
    { commit, rootState },
    {
      beginAt,
      endAt,
      dispatchType,
    }: {
      beginAt?: string;
      endAt?: string;
      dispatchType: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const payload = {
        companyId,
        beginAt,
        endAt,
      };
      switch (dispatchType) {
        case "admission":
          {
            const summary = await SurveyService.getAdmissionOverviewDetails(
              payload,
            );
            commit("SET_ADMISSION_OVERVIEW", summary);
          }
          break;
        case "discharge":
          {
            const summary = await SurveyService.getDischargeOverviewDetails(
              payload,
            );
            commit("SET_DISCHARGE_OVERVIEW", summary);
          }
          break;
        case "satisfaction":
          {
            const summary = await SurveyService.getSatisfactionOverviewDetails(
              payload,
            );
            commit("SET_SATISFACTION_OVERVIEW", summary);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchOverviewDetails: async (
    { commit, rootState },
    {
      beginAt,
      endAt,
      dispatchType,
      overviewId,
    }: {
      beginAt?: string;
      endAt?: string;
      dispatchType: string;
      overviewId: number;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const payload = {
        companyId,
        beginAt,
        endAt,
        overviewId,
      };
      switch (dispatchType) {
        case "admission":
          {
            const overview = await SurveyService.getAdmissionSurveyOverviewDetails(
              payload,
            );
            commit("SET_ADMISSION_OVERVIEW_DETAILS", overview);
          }
          break;
        case "discharge":
          {
            const overview = await SurveyService.getDischargeSurveyOverviewDetails(
              payload,
            );
            commit("SET_DISCHARGE_OVERVIEW_DETAILS", overview);
          }
          break;
        case "satisfaction":
          {
            const overview = await SurveyService.getSatisfactionSurveyOverviewDetails(
              payload,
            );
            commit("SET_SATISFACTION_OVERVIEW_DETAILS", overview);
          }
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  clearSummaryDetails: ({ commit }) => {
    commit("SET_ADMISSION_OVERVIEW_DETAILS", null);
    commit("SET_DISCHARGE_OVERVIEW_DETAILS", null);
    commit("SET_SATISFACTION_OVERVIEW_DETAILS", null);
  },
};

export const survey: Module<SurveyState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
